import { Box, Grid } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import { settings } from "app";
import BackButtonToolbar from "features/BackButtonToolbar";
import {
  FunctionField,
  Labeled,
  Show,
  SimpleShowLayout,
  TextField,
  useRecordContext,
} from "react-admin";
import { DoctorantStatusChoices, DoctorantStatusColorMap } from "utils/choices";
import StatusChip from "../../Partials/StatusChip";
import { instructionLanguageLabel, studyTypeLabel } from "../../seatLabel";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import PersonalInfo from "../../DoctorantCandidacies/Show/PersonalInfo";
import PlaceOfBirth from "../../DoctorantCandidacies/Show/PlaceOfBirth";
import PreviousEducations from "../../DoctorantCandidacies/Show/PreviousEducations";
import DocumentsSection from "./DocumentsSection";
import InterruptionsSection from "./InterruptionsSection";
import ReportsSection from "./ReportsSection";
import DebtsSection from "./DebtsSection";
import ActionButtons from "./ActionButtons";
import DefenseSection from "./DefenseSection";
import ChangesSection from "./ChangesSection";
import PersonalInfoEditDialog from "./PersonalInfoEditDialog";
import TopicEditDialog from "./TopicEditDialog";
import SupervisorsEditDialog from "./SupervisorsEditDialog";
import PreviousEducationEditDialog from "./PreviousEducationEditDialog";
import { DoctorantStatus } from "utils/constants";
import DoctoralDateField, { formatDoctoralDate } from "../../Partials/DoctoralDateField";
import { MoneyField } from "server/components/Fields/MoneyField";
import { SectionTitle } from "features/SectionTitle";

const SectionEditAction = ({ children }) => (
  <SecuredComponent permission={permissions.canUpdateDoctorants}>
    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>{children}</Box>
  </SecuredComponent>
);

const Header = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <Labeled label="Кампания">
          <TextField source="campaignNameBg" emptyText="—" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Факултет">
          <TextField source="structuralUnitName" emptyText="—" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Катедра">
          <TextField source="departmentName" emptyText="—" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Докторантска програма">
          <TextField source="doctoralProgramNameBg" emptyText="—" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Форма на обучение">
          <FunctionField render={r => studyTypeLabel(r.studyTypeId) || "—"} />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Език на обучение">
          <FunctionField render={r => instructionLanguageLabel(r.instructionLanguageId) || "—"} />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Годишна такса">
          <MoneyField source="annualFee" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Заповед за зачисляване">
          <FunctionField
            render={r => (r.orderNumber ? `${r.orderNumber} / ${formatDoctoralDate(r.orderDate)}` : "—")}
          />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Срок на докторантурата до">
          <DoctoralDateField source="termEndsOn" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Статус">
          <FunctionField
            render={r => (
              <StatusChip
                status={r.status}
                choices={DoctorantStatusChoices}
                colorMap={DoctorantStatusColorMap}
              />
            )}
          />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={3}>
        <Labeled label="Записан на">
          <DoctoralDateField source="enrolledOn" showTime />
        </Labeled>
      </Grid>
      {record.defenseDeadlineOn && (
        <Grid item xs={12} md={3}>
          <Labeled label="Срок за защита">
            <DoctoralDateField source="defenseDeadlineOn" />
          </Labeled>
        </Grid>
      )}
      {record.dismissalProtocolNumber && (
        <>
          <Grid item xs={12} md={3}>
            <Labeled label="Протокол за отчисляване №">
              <TextField source="dismissalProtocolNumber" />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={3}>
            <Labeled label="Дата на протокола за отчисляване">
              <DoctoralDateField source="dismissalProtocolDate" />
            </Labeled>
          </Grid>
        </>
      )}
    </Grid>
  );
};

const Emails = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Grid container spacing={2} sx={{ marginTop: 0 }}>
      <Grid item xs={12} md={4}>
        <Labeled label="Имейл (потребителско име)">
          <TextField source="email" emptyText="—" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Университетски имейл">
          <TextField source="externalEmail" emptyText="—" />
        </Labeled>
      </Grid>
    </Grid>
  );
};

const SupervisorsSection = () => {
  const record = useRecordContext();
  const supervisors = record?.supervisors ?? [];
  if (supervisors.length === 0) return <em>Няма данни</em>;

  return (
    <Grid container spacing={2}>
      {supervisors.map((supervisor, index) => (
        <Grid item xs={12} md={4} key={supervisor.employeeId}>
          <Labeled label={`Научен ръководител ${supervisors.length > 1 ? index + 1 : ""}`.trim()}>
            <FunctionField
              render={() =>
                supervisor.fullName
                  ? [supervisor.scientificDegreeShortNameBg, supervisor.fullName]
                      .filter(Boolean)
                      .join(" ")
                  : `Служител №${supervisor.employeeId}`
              }
            />
          </Labeled>
        </Grid>
      ))}
    </Grid>
  );
};

const DefenseBlock = ({ shadow }) => {
  const record = useRecordContext();
  const canShow =
    record?.status === DoctorantStatus.DismissedWithRightToDefend ||
    record?.status === DoctorantStatus.DefendedSuccessfully;
  if (!canShow) return null;

  return (
    <>
      <SectionTitle
        title="Защита"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <Box sx={shadow}>
        <DefenseSection />
      </Box>
    </>
  );
};

const ShowDoctorant = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const shadow = isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox;

  return (
    <Show title="Докторант" actions={<BackButtonToolbar />}>
      <SimpleShowLayout>
        <ActionButtons />

        <SectionTitle
          title="Данни за обучението"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <Header />
        </Box>

        <SectionTitle
          title="Лични данни"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <SectionEditAction>
            <PersonalInfoEditDialog />
          </SectionEditAction>
          <PersonalInfo />
          <PlaceOfBirth />
          <Emails />
        </Box>

        <SectionTitle
          title="Тема на дисертацията"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <SectionEditAction>
            <TopicEditDialog />
          </SectionEditAction>
          <TextField source="topic" emptyText="—" />
        </Box>

        <SectionTitle
          title="Научни ръководители"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <SectionEditAction>
            <SupervisorsEditDialog />
          </SectionEditAction>
          <SupervisorsSection />
        </Box>

        <SectionTitle
          title="Предходно образование"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <SectionEditAction>
            <PreviousEducationEditDialog />
          </SectionEditAction>
          <PreviousEducations />
        </Box>

        <SectionTitle
          title="Прекъсвания"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <InterruptionsSection />
        </Box>

        <SectionTitle
          title="Документи"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <DocumentsSection />
        </Box>

        <SectionTitle
          title="Отчети"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <ReportsSection />
        </Box>

        <SectionTitle
          title="Плащания"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <DebtsSection />
        </Box>

        <DefenseBlock shadow={shadow} />

        <SectionTitle
          title="Журнал на промените"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <ChangesSection />
        </Box>
      </SimpleShowLayout>
    </Show>
  );
};

export default ShowDoctorant;
